import { useState, memo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { File, Car, Coffee, Wrench, ShoppingBag, X, Loader2, AlertTriangle, Eye } from "lucide-react";
import { Receipt } from "@/hooks/useReceipts";

const categoryIcons: Record<string, any> = {
  Gas: Car,
  Meals: Coffee,
  Repairs: Wrench,
  Supplies: ShoppingBag,
};

const categoryColors: Record<string, string> = {
  Gas: "bg-blue-100 text-blue-700",
  Meals: "bg-orange-100 text-orange-700",
  Repairs: "bg-purple-100 text-purple-700",
  Supplies: "bg-green-100 text-green-700",
};

const CONFIDENCE_THRESHOLD = 0.85;

interface ReceiptCardProps {
  receipt: Receipt;
  onDelete: (id: string) => Promise<void>;
  onView: (receipt: Receipt) => void;
  isDeleting: boolean;
}

export const ReceiptCard = memo(function ReceiptCard({ 
  receipt, 
  onDelete, 
  onView,
  isDeleting 
}: ReceiptCardProps) {
  const Icon = categoryIcons[receipt.category] || File;
  const colorClass = categoryColors[receipt.category] || "bg-gray-100 text-gray-700";

  return (
    <Card className="p-4 hover:shadow-md transition-shadow relative">
      {receipt.status === 'needs_review' && (
        <Badge variant="destructive" className="absolute top-2 right-2 gap-1">
          <AlertTriangle className="w-3 h-3" />
          Needs Review
        </Badge>
      )}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(receipt.id)}
          disabled={isDeleting}
          className="h-8 w-8 p-0"
        >
          {isDeleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
        </Button>
      </div>
      <h4 className="font-semibold text-foreground mb-1 truncate">
        {receipt.merchant_name || "Unknown Merchant"}
      </h4>
      <p className="text-sm text-muted-foreground mb-2">{receipt.category}</p>
      <div className="flex items-center justify-between">
        <p className="text-lg font-bold text-primary">
          ${(receipt.total || receipt.amount).toFixed(2)}
          {receipt.manual_total && (
            <span className="text-xs text-muted-foreground ml-1">(manual)</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">{receipt.date}</p>
      </div>
      {receipt.subtotal > 0 && (
        <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
          Subtotal: ${receipt.subtotal.toFixed(2)} + Tax: ${receipt.tax.toFixed(2)}
        </div>
      )}
      {receipt.confidence_score !== undefined && receipt.confidence_score < CONFIDENCE_THRESHOLD && (
        <div className="text-xs text-muted-foreground mt-2 pt-2 border-t flex items-center justify-between">
          <span>AI Confidence: {(receipt.confidence_score * 100).toFixed(0)}%</span>
          {receipt.version && receipt.version > 1 && (
            <span className="text-xs">v{receipt.version}</span>
          )}
        </div>
      )}
      <div className="mt-2 pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onView(receipt)}
        >
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Button>
      </div>
    </Card>
  );
});
